import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Phone, Mail, Building2, Filter, X, ChevronRight, ArrowLeft, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [searchQuery, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category_id', selectedCategory);
      
      const res = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(res.data);
    } catch (e) {
      console.error("Error fetching businesses:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    navigate(`/recherche?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    navigate('/recherche');
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '';
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link 
              to="/inscrire" 
              className="btn-coral text-sm py-2 px-6"
              data-testid="register-btn"
            >
              Inscrire mon entreprise
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-lagoon-600 hover:text-lagoon-900 mb-8"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à l'accueil</span>
          </Link>

          {/* Search & Filters */}
          <div className="glass-card rounded-2xl p-6 mb-8">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher une entreprise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              
              <div className="flex gap-4">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full md:w-[200px] h-12 bg-white/50 border-lagoon-100 rounded-xl" data-testid="category-filter">
                    <SelectValue placeholder="Toutes catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button type="submit" className="btn-primary h-12" data-testid="search-btn">
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Active Filters */}
            {(searchQuery || (selectedCategory && selectedCategory !== 'all')) && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-lagoon-100">
                <span className="text-sm text-gray-500">Filtres actifs:</span>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-lagoon-50 text-lagoon-900 rounded-full text-sm">
                    "{searchQuery}"
                  </span>
                )}
                {selectedCategory && selectedCategory !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-lagoon-50 text-lagoon-900 rounded-full text-sm">
                    {getCategoryName(selectedCategory)}
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-coral hover:text-coral-600 text-sm font-medium ml-2"
                  data-testid="clear-filters-btn"
                >
                  Effacer tout
                </button>
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900">
              {loading ? 'Recherche en cours...' : `${businesses.length} entreprise${businesses.length > 1 ? 's' : ''} trouvée${businesses.length > 1 ? 's' : ''}`}
            </h1>
          </div>

          {/* Results Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="glass-card rounded-2xl p-6 animate-pulse">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl mb-4" />
                  <div className="h-6 bg-gray-200 rounded mb-2 w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : businesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business, index) => (
                <motion.div
                  key={business.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link 
                    to={`/entreprise/${business.id}`}
                    className={`glass-card rounded-2xl p-6 block hover:border-lagoon-200 group relative ${business.is_featured ? 'border-golden/30 ring-2 ring-golden/20' : ''}`}
                    data-testid={`business-card-${business.id}`}
                  >
                    {/* Featured Badge */}
                    {business.is_featured && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-golden text-lagoon-900 rounded-full text-xs font-bold">
                        <Star className="w-3 h-3 fill-lagoon-900" />
                        Vedette
                      </div>
                    )}
                    
                    <div className="flex items-start gap-4 mb-4">
                      {business.logo_base64 ? (
                        <img 
                          src={business.logo_base64} 
                          alt={business.name}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-xl logo-placeholder text-xl">
                          {business.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-lg font-semibold text-lagoon-900 truncate group-hover:text-lagoon">
                          {business.name}
                        </h3>
                        <span className="inline-block px-2 py-0.5 bg-lagoon-50 text-lagoon-600 rounded text-xs mt-1">
                          {business.category_name}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {business.description}
                    </p>
                    
                    <div className="space-y-2 text-sm text-gray-500">
                      {business.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-lagoon" />
                          <span>{business.phone}</span>
                        </div>
                      )}
                      {business.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-lagoon" />
                          <span className="truncate">{business.email}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-lagoon text-sm font-medium group-hover:underline">
                        Voir les détails
                      </span>
                      <ChevronRight className="w-4 h-4 text-lagoon group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 rounded-full bg-lagoon-50 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-lagoon" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-lagoon-900 mb-2">
                Aucune entreprise trouvée
              </h3>
              <p className="text-gray-500 mb-6">
                Essayez de modifier vos critères de recherche
              </p>
              <Button onClick={clearFilters} className="btn-primary" data-testid="clear-and-retry-btn">
                Voir toutes les entreprises
              </Button>
            </motion.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white border-t border-lagoon-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-gray-500 text-sm">© 2026 Annuaire Polynésien</p>
          <Link to="/" className="text-lagoon hover:text-lagoon-600 text-sm">Accueil</Link>
        </div>
      </footer>
    </div>
  );
}
